import React, { useState, useMemo } from "react";
import "./ai-tasks.css";
import tiger0 from "../../images/tiger0.png";
import {
  getTimeAgo,
  dateToWords,
  buildConversationPath,
} from "../../util/helperFunctions";
import ApiService from "../../api/apiService";
import { useNavigate } from "react-router-dom";

const PRIORITY_CONFIG = {
  high: {
    label: "High",
    color: "#dc2626",
    bg: "rgba(220, 38, 38, 0.1)",
    icon: "fa-arrow-up",
  },
  medium: {
    label: "Medium",
    color: "#d97706",
    bg: "rgba(217, 119, 6, 0.1)",
    icon: "fa-minus",
  },
  low: {
    label: "Low",
    color: "#16a34a",
    bg: "rgba(22, 163, 74, 0.1)",
    icon: "fa-arrow-down",
  },
};

function getDueDateMeta(dueDate) {
  if (!dueDate) return null;
  const due = new Date(dueDate);
  due.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffDays = Math.round((due - today) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return { label: `Overdue · ${dateToWords(dueDate)}`, color: "#dc2626" };
  if (diffDays === 0) return { label: "Due today", color: "#d97706" };
  if (diffDays <= 2) return { label: `Due ${dateToWords(dueDate)}`, color: "#d97706" };
  return { label: `Due ${dateToWords(dueDate)}`, color: "#888" };
}



function AITasksPanel({ isOpen, tasks, onToggle, onClose }) {
  const [filter, setFilter] = useState("all");

  const sorted = useMemo(
    () =>
      [...tasks].sort((a, b) => {
        const pa = PRIORITY_CONFIG[a.priority?.toLowerCase()] ?? 3;
        const pb = PRIORITY_CONFIG[b.priority?.toLowerCase()] ?? 3;
        return pa - pb;
      }),
    [tasks],
  );

  const filtered = useMemo(
    () =>
      filter === "all"
        ? sorted
        : sorted.filter((t) => t.priority?.toLowerCase() === filter),
    [sorted, filter],
  );

  const counts = useMemo(
    () => ({
      high: tasks.filter((t) => t.priority?.toLowerCase() === "high").length,
      medium: tasks.filter((t) => t.priority?.toLowerCase() === "medium")
        .length,
      low: tasks.filter((t) => t.priority?.toLowerCase() === "low").length,
    }),
    [tasks],
  );

  return (
    <div
      className={`ai-tasks-overlay${isOpen ? " ai-tasks-overlay--open" : ""}`}
    >
      <div className="ai-tasks__header">
        <img src={tiger0} alt="Tiger ZERO AI" className="ai-tasks__header-icon" />
        <div className="ai-tasks__header-info">
          <span className="ai-tasks__header-title">AI Tasks</span>
          <span className="ai-tasks__header-subtitle">
            Tiger ZERO AI To-Do List
          </span>
        </div>
        <button
          className="ai-tasks__close-btn"
          onClick={onClose}
          aria-label="Close tasks panel"
        >
          <i className="fa fa-times" />
        </button>
      </div>

      {tasks.length > 0 && (
        <>
          <div className="ai-tasks__stats">
            {counts.high > 0 && (
              <span className="ai-tasks__stat ai-tasks__stat--high">
                <i className="fa fa-arrow-up" />
                {counts.high} High
              </span>
            )}
            {counts.medium > 0 && (
              <span className="ai-tasks__stat ai-tasks__stat--medium">
                <i className="fa fa-minus" />
                {counts.medium} Medium
              </span>
            )}
            {counts.low > 0 && (
              <span className="ai-tasks__stat ai-tasks__stat--low">
                <i className="fa fa-arrow-down" />
                {counts.low} Low
              </span>
            )}
            <span className="ai-tasks__stat-total">
              {tasks.length} task{tasks.length !== 1 ? "s" : ""} pending
            </span>
          </div>

          <div className="ai-tasks__filters">
            {["all", "high", "medium", "low"].map((f) => (
              <button
                key={f}
                className={`ai-tasks__filter-btn${
                  filter === f ? " ai-tasks__filter-btn--active" : ""
                }`}
                onClick={() => setFilter(f)}
              >
                {f === "all"
                  ? `All (${tasks.length})`
                  : `${f.charAt(0).toUpperCase() + f.slice(1)}${
                      counts[f] ? ` (${counts[f]})` : ""
                    }`}
              </button>
            ))}
          </div>
        </>
      )}

      <div className="ai-tasks__body">
        {tasks.length === 0 ? (
          <div className="ai-tasks__empty">
            <img src={tiger0} alt="" className="ai-tasks__empty-icon" />
            <p className="ai-tasks__empty-title">No tasks yet</p>
            <small className="ai-tasks__empty-subtitle">
              Mention <strong>Tiger ZERO AI</strong> in any message to auto-create
              a task.
            </small>
          </div>
        ) : filtered.length === 0 ? (
          <div className="ai-tasks__empty">
            <p className="ai-tasks__empty-title">No {filter} priority tasks</p>
          </div>
        ) : (
          filtered.map((task) => (
            <TaskItem key={task.id} task={task} onToggle={onToggle} />
          ))
        )}
      </div>
    </div>
  );
}

function TaskItem({ task, onToggle }) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const priority = task.priority?.toLowerCase();
  const config = PRIORITY_CONFIG[priority] || {
    label: "Normal",
    color: "#6b7280",
    bg: "rgba(107, 114, 128, 0.1)",
    icon: "fa-flag",
  };
  const hasLongDesc = task.description && task.description.length > 100;

  const handleTaskClick = async () => {
    const msgIndex = await ApiService.message.getMessagePosition(
      task.conversation?.id,
      task.message?.id,
    );

    const { success, response } = msgIndex;
    const { index, query } = response;

    if (!success) return;
    navigate(
      buildConversationPath(task.conversation?.id, {
        q: task.message?.id,
        i: index,
        _q: query,
      }),
    );
  };

  const dueMeta = getDueDateMeta(task.dueDate);

  return (
    <div
      className={`ai-task-item${task.isDone ? " ai-task-item--done" : ""}`}
      style={{ "--priority-color": config.color }}
      onClick={handleTaskClick}
    >
      <div className="ai-task-item__top">
        <span
          className="ai-task-item__priority-badge"
          style={{ color: config.color, background: config.bg }}
        >
          <i className={`fa ${config.icon}`} />
          {config.label}
        </span>
        <span className="ai-task-item__time">
          {getTimeAgo(task.createdAt) || "—"}
        </span>
      </div>

      <p className="ai-task-item__title">{task.title}</p>

      {task.description && task.description !== task.title && (
        <div className="ai-task-item__description-wrap">
          <p
            className={`ai-task-item__description${
              expanded ? " ai-task-item__description--expanded" : ""
            }`}
          >
            {task.description}
          </p>
          {hasLongDesc && (
            <button
              className="ai-task-item__expand-btn"
              onClick={() => setExpanded((v) => !v)}
            >
              {expanded ? "Show less" : "Show more"}
            </button>
          )}
        </div>
      )}

      <div className="ai-task-item__footer">
        <span className="ai-task-item__conversation">
          <i className="fa fa-comments" />
          {task.conversation?.name || "General Chat"}
        </span>
        {dueMeta && (
          <span
            className="ai-task-item__due-date"
            style={{ color: dueMeta.color }}
          >
            <i className="fa fa-calendar" />
            {dueMeta.label}
          </span>
        )}
      </div>
    </div>
  );
}

export default AITasksPanel;
